import { useEffect, useRef, useState } from 'react';
import { useRecoilState, useRecoilValue, useSetRecoilState } from 'recoil';
import style from './image3dProdBoard.module.css';
import rendering from '../../../assets/rendering.png';
import views from '../../../assets/views.png';
import { imaginefacePreview } from '../../../utils/net';
import {
    cameraParamsAtom,
    currentPreviewIndexAtom,
    isOpenImg3dGenerateAtom,
    showRegenerateAtom,
    showThreePalaceGridAtom,
    taskDetailAtom,
    isPurchasedAtom,
    modalObjAtom
} from '../../../store';
import { useTips } from '../../../common/GlobalTips';
import { ImageOverlay } from '../../../common/ImageOverlay';
import { XScroll } from '../../widgets/XScroll';
import { isNotEmpty } from '../../../utils/format';
import { sideColorArr } from '../../../utils/map';
import * as Sentry from "@sentry/react";
import { useThrottle } from '../../../utils/hooks';
import { useTranslation } from "react-i18next";

function Image3dProdBoard() {
    const taskDetail = useRecoilValue(taskDetailAtom);
    const isPurchased = useRecoilValue(isPurchasedAtom);
    const isOpenImg3dGenerate = useRecoilValue(isOpenImg3dGenerateAtom);
    const setCameraParams = useSetRecoilState(cameraParamsAtom);
    const [currentPreviewIndex, setCurrentPreviewIndex] = useRecoilState(currentPreviewIndexAtom);
    const [showThreePalaceGrid, setShowThreePalaceGrid] = useRecoilState(showThreePalaceGridAtom);
    const [showRegenerate, setShowRegenerate] = useRecoilState(showRegenerateAtom);
    const [cameraInfoRaw, setCameraInfoRaw] = useState({
        intrinsic: [],
        extrinsic: [],
    });
    const [imgUrlsArr, setImgUrlsArr] = useState([]);
    const [previewImgArr, setPreviewImgArr] = useState([]);
    const [prevData, setPrevData] = useState(-1);
    const [cursorPos, setCursorPos] = useState({ x: 160, y: 130 });
    const topBoxRef = useRef(null);
    const mainBoxRef = useRef(null);
    const prevewImgBoxRef = useRef(null);
    const filterBoxRef = useRef(null);
    const intervalRef = useRef(null);
    const timeoutId = useRef(null);
    const tip = useTips();
    const { t } = useTranslation();
    const photoItemRefs = new Array(9).fill().map(() => useRef(null))
    const [grabbing, setGrabbing] = useState(false);
    const [informationUrl, setInformationUrl] = useState(null);
    const [done, setDone] = useState(false);
    const [infomationDone, setInfomationDone] = useState(false);
    const [modalObj, setModalObj] = useRecoilState(modalObjAtom);
    const clipPathValueA = `inset(0px calc(100%-${cursorPos.x}px) 0px 0px)`;
    const clipPathValueB = `inset(0px 0px calc(100% - ${cursorPos.y}px) ${cursorPos.x}px)`;

    useEffect(() => {
        if (taskDetail?.image_urls?.length) {
            setImgUrlsArr(taskDetail.image_urls);
        } else {
            setImgUrlsArr(['']);
        }
        return () => {
            if (!isOpenImg3dGenerate) {
                setImgUrlsArr([]);
            }
            clearInterval(intervalRef.current);
            setShowRegenerate(false);
            setShowThreePalaceGrid(true);
            setCurrentPreviewIndex(0);
        };

    }, []);

    useEffect(() => {
        if (imgUrlsArr.length && !done) {
            const fun = async () => {
                try {
                    // interval to query renderImg
                    let res;
                    const intervalQuery = async () => {
                        clearInterval(intervalRef.current);
                        intervalRef.current = setInterval(async () => {
                            res = await imaginefacePreview(taskDetail.task_uuid);
                            // return interval
                            if (!res?.data?.error && res?.data?.information !== "FILE_NOT_FOUND") {
                                const filesResult = res.data.files;
                                setInformationUrl(res.data.information);
                                if (filesResult.length >= imgUrlsArr.length) {
                                    let flag = true;

                                    const updatePreviewImgArr = async () => {
                                        const updatedPreviewImgArr = [];
                                        for (let i = 0; i < filesResult.length; i++) {
                                            const accessItem = filesResult[i];
                                            if (previewImgArr[i]?.access) {
                                                updatedPreviewImgArr.push(previewImgArr[i]);
                                                continue;
                                            }
                                            if (
                                                accessItem.face !== 'FILE_NOT_FOUND'
                                                && accessItem.render !== 'FILE_NOT_FOUND'
                                                && accessItem.render_statue !== 'FILE_NOT_FOUND'
                                            ) {
                                                updatedPreviewImgArr.push({
                                                    face: accessItem.face,
                                                    render: accessItem.render,
                                                    render_statue: accessItem.render_statue,
                                                    access: true,
                                                    loaded: [false, false, false],
                                                });
                                            } else {
                                                flag = false;
                                                updatedPreviewImgArr.push(previewImgArr[i]);
                                            }
                                        }

                                        await Promise.all(updatedPreviewImgArr);

                                        setPreviewImgArr(updatedPreviewImgArr);
                                    };

                                    updatePreviewImgArr();
                                    if (flag) clearInterval(intervalRef.current);
                                }
                            }
                        }, 2000);
                    };
                    await intervalQuery();
                } catch (error) {
                    Sentry.captureException(error)
                }
            };
            fun();
            setDone(true);
        }

    }, [imgUrlsArr]);

    useEffect(() => {
        if (informationUrl && !infomationDone) {
            const queryCamera = async () => {
                const fetchCamInfo = async (url) => {
                    try {
                        const response = await fetch(url);
                        const data = await response.json();
                        return data;
                    } catch (error) {
                        Sentry.captureException(error)
                        console.error(error);
                    }
                };
                const information = await fetchCamInfo(informationUrl);
                if (information?.intrinsic?.length && information.extrinsic?.length) {
                    setInfomationDone(true);
                    setCameraInfoRaw({
                        intrinsic: information.intrinsic,
                        extrinsic: information.extrinsic,
                    });
                }
            };
            queryCamera();
        }

    }, [informationUrl]);

    useEffect(() => {
        if (taskDetail.model_preview_info?.error) {
            setShowThreePalaceGrid(true);
        }

    }, [taskDetail.model_preview_info]);

    useEffect(() => {
        if (isNotEmpty(cameraInfoRaw.intrinsic) && isNotEmpty(cameraInfoRaw.extrinsic)) {
            setCameraParams({
                intrinsic: cameraInfoRaw.intrinsic[currentPreviewIndex],
                extrinsic: cameraInfoRaw.extrinsic[currentPreviewIndex],
            });
        }

    }, [cameraInfoRaw, currentPreviewIndex]);

    useEffect(() => {
        const defaultX = filterBoxRef.current.clientWidth / 2;
        const defaultY = filterBoxRef.current.clientHeight / 2;
        setCursorPos({ x: defaultX, y: defaultY });
    }, [showThreePalaceGrid, currentPreviewIndex, showRegenerate]);

    useEffect(() => {
        if (prevData !== -1) {
            const box = photoItemRefs[prevData].current;
            box.scrollIntoView({
                behavior: 'smooth',
                inline: 'start',
            });
        }

    }, [currentPreviewIndex]);

    useEffect(() => {
        // 检查 ref 是否存在
        const prevewImgBox = prevewImgBoxRef.current;
        const filterBox = filterBoxRef.current;

        // 如果任一元素不存在，直接返回
        if (!prevewImgBox || !filterBox) {
            return;
        }

        const updateStyles = () => {
            if (showThreePalaceGrid) {
                if (timeoutId.current) {
                    clearTimeout(timeoutId.current);
                }

                // 安全地更新样式
                if (prevewImgBox) {
                    prevewImgBox.style.opacity = '0';
                    prevewImgBox.style.filter = 'blur(10px)';
                    prevewImgBox.style.zIndex = '0';
                }

                timeoutId.current = setTimeout(() => {
                    if (filterBox) {
                        filterBox.style.opacity = '1';
                        filterBox.style.filter = '';
                        filterBox.style.zIndex = '2';
                    }
                }, 200);
            } else {
                if (timeoutId.current) {
                    clearTimeout(timeoutId.current);
                }

                // 安全地更新样式
                if (filterBox) {
                    filterBox.style.opacity = '0';
                    filterBox.style.filter = 'blur(10px)';
                    filterBox.style.zIndex = '0';
                }

                timeoutId.current = setTimeout(() => {
                    if (prevewImgBox) {
                        prevewImgBox.style.opacity = '1';
                        prevewImgBox.style.filter = '';
                        prevewImgBox.style.zIndex = '2';
                    }
                }, 200);
            }
        };

        // 尝试更新样式
        try {
            updateStyles();
        } catch (error) {
            console.error('Error updating styles:', error);
        }

        // 清理函数
        return () => {
            if (timeoutId.current) {
                clearTimeout(timeoutId.current);
            }
        };
    }, [showThreePalaceGrid]);

    const getSrc = (key) => {
        if (
            previewImgArr.length
            && previewImgArr[currentPreviewIndex]
            && previewImgArr[currentPreviewIndex].access
        ) {
            switch (key) {
                case 0:
                    return previewImgArr[currentPreviewIndex].face;
                case 1:
                    return previewImgArr[currentPreviewIndex].render;
                case 2:
                    return previewImgArr[currentPreviewIndex].render_statue;
                default:
                    break;
            }
        }
    };

    const throttlMove = useThrottle((e, operate, startX, startY, InitialX, InitialY, maxX, maxY) => {
        const endX = operate === 'mouse' ? e.clientX : e.touches[0].clientX;
        const endY = operate === 'mouse' ? e.clientY : e.touches[0].clientY;
        const diffX = endX - startX + InitialX;
        const diffY = endY - startY + InitialY;
        let newX = diffX;
        let newY = diffY;
        if (diffX < 0) {
            newX = 0;
        } else if (diffX > maxX) {
            newX = maxX;
        }

        if (diffY < 0) {
            newY = 0;
        } else if (diffY > maxY) {
            newY = maxY;
        }
        setCursorPos({ x: newX, y: newY });
    }, 10)

    const handleStart = (e, operate) => {
        if (operate === 'mouse' && e.button !== 0) return;
        if (operate === 'touch' && e.touches.length !== 1) return;
        setGrabbing(true);
        const startX = operate === 'mouse' ? e.clientX : e.touches[0].clientX;
        const startY = operate === 'mouse' ? e.clientY : e.touches[0].clientY;
        const InitialX = cursorPos.x;
        const InitialY = cursorPos.y;
        const maxX = filterBoxRef.current.clientWidth;
        const maxY = filterBoxRef.current.clientHeight;
        const handleMove = (e) => throttlMove(e, operate, startX, startY, InitialX, InitialY, maxX, maxY)
        const handleEnd = () => {
            setGrabbing(false);
            if (operate === 'mouse') {
                document.removeEventListener('mousemove', handleMove);
                document.removeEventListener('mouseup', handleEnd);
            } else {
                document.removeEventListener('touchmove', handleMove);
                document.removeEventListener('touchend', handleEnd);
                document.removeEventListener('touchcancel', handleEnd);
            }
        }
        if (operate === 'mouse') {
            document.addEventListener('mousemove', handleMove);
            document.addEventListener('mouseup', handleEnd);
        } else {
            document.addEventListener('touchmove', handleMove);
            document.addEventListener('touchend', handleEnd);
            document.addEventListener('touchcancel', handleEnd);
        }
        if (e.preventDefault) {
            e.preventDefault();
        }
    };

    const handlerClickPhoto = (index,) => {
        if (index === currentPreviewIndex) {
            setCameraParams({
                intrinsic: cameraInfoRaw.intrinsic[currentPreviewIndex],
                extrinsic: cameraInfoRaw.extrinsic[currentPreviewIndex],
            });
            return;
        }
        setPrevData(index);
        setTimeout(() => {
            setCurrentPreviewIndex(index);
        }, 200);
        if (showThreePalaceGrid) {
            clearTimeout(timeoutId.current);
            filterBoxRef.current.style.opacity = 0.3;
            filterBoxRef.current.style.filter = 'blur(10px)';
            filterBoxRef.current.style.zIndex = 0;
            timeoutId.current = setTimeout(() => {
                filterBoxRef.current.style.opacity = 1;
                filterBoxRef.current.style.filter = '';
                filterBoxRef.current.style.zIndex = 1;
            }, 200);
        } else {
            clearTimeout(timeoutId.current);
            prevewImgBoxRef.current.style.opacity = 0.3;
            prevewImgBoxRef.current.style.filter = 'blur(10px)';
            prevewImgBoxRef.current.style.zIndex = 0;
            timeoutId.current = setTimeout(() => {
                prevewImgBoxRef.current.style.opacity = 1;
                prevewImgBoxRef.current.style.filter = '';
                prevewImgBoxRef.current.style.zIndex = 1;
            }, 200);
        }
    };

    const handlerRegenerate = () => {
        setModalObj({
            ...modalObj,
            state: false
        })
    };

    const handlerImgLoaded = (index) => {
        if (
            previewImgArr.length
            && previewImgArr[currentPreviewIndex]
            && previewImgArr[currentPreviewIndex].access
        ) {
            const newArr = previewImgArr.slice();
            newArr[currentPreviewIndex].loaded[index] = true;
            setPreviewImgArr(newArr);
        }
    };

    const handlerShowRegTips = () => tip({
        type: 'primary',
        content: t('TIP_PRIMARY_MODEL_REPLACE'),
        yes: handlerRegenerate,
        no: (f) => f,
    })

    const handlerToggleThreeGrid = () => setShowThreePalaceGrid(pre => !pre)

    return (
        <div className={style.Img3dProdWrapper}>
            <div className={style.titleBox}>
                <div className={style.title}>Image-to-3D</div>
                {showRegenerate && (
                    <div
                        onPointerDown={handlerShowRegTips}
                        className={style.reGenerate}
                    >
                        Regenerate
                    </div>
                )}
            </div>
            <div className={style.mainBox} ref={mainBoxRef}>
                <div className={style.switchBox}>
                    <div>
                        {showThreePalaceGrid ? 'Original' : 'Preview'}
                        {' '}
                    </div>
                    {isPurchased && (
                        <div
                            className={style.switch}
                            onClick={handlerToggleThreeGrid}>
                            <img src={showThreePalaceGrid ? views : rendering} alt="" />
                        </div>
                    )}

                </div>
                <div
                    className={style.topBox}
                    onMouseDown={(e) => handleStart(e, 'mouse')}
                    onTouchStart={(e) => handleStart(e, 'touch')}
                    ref={topBoxRef}
                >
                    <div
                        ref={filterBoxRef}
                        className={style.filterBox}
                        style={{
                            cursor: grabbing ? 'grabbing' : 'grab',
                        }}
                    >
                        {previewImgArr[currentPreviewIndex]
                            && previewImgArr[currentPreviewIndex].loaded.every((item) => item) ? (
                            <>
                                {new Array(3).fill(0).map((item, index) => (
                                    <div
                                        key={`preview${index}`}
                                        className={style.imgBox}
                                        style={{
                                            clipPath: `${index === 0
                                                ? `var(--clip-path-value, ${clipPathValueA})`
                                                : index === 1
                                                    ? `var(--clip-path-value, ${clipPathValueB})`
                                                    : `inset(${cursorPos.y}px 0px 0px ${cursorPos.x}px)`
                                                }`,
                                        }}
                                    >
                                        <img src={getSrc(index)} alt="" />
                                    </div>
                                ))}
                                <div
                                    style={{
                                        position: 'absolute',
                                        top: `${cursorPos.y}px`,
                                        left: `${cursorPos.x}px`,
                                        width: '100%',
                                        height: '1px',
                                        backgroundColor: 'white',
                                    }}
                                />
                                <div
                                    style={{
                                        position: 'absolute',
                                        top: '0',
                                        width: '1px',
                                        height: '100%',
                                        backgroundColor: 'white',
                                        left: `${cursorPos.x}px`,
                                    }}
                                />
                                <div
                                    style={{
                                        top: `${cursorPos.y}px`,
                                        left: `${cursorPos.x}px`,
                                    }}
                                    className={style.FrameBox}
                                >
                                    <span />
                                    <span />
                                    <span />
                                </div>
                            </>
                        ) : (
                            <div
                                style={{
                                    position: 'relative',
                                    width: '100%',
                                    height: 'auto',
                                    aspectRatio: '1/1.15',
                                }}
                            >
                                <div className={style.overlayBox}>
                                    <div className={style.skeleton} />
                                </div>
                            </div>
                        )}
                        {new Array(3).fill(0).map((item, index) => (
                            <img
                                src={getSrc(index)}
                                key={index}
                                alt=""
                                onLoad={handlerImgLoaded.bind(null, index)}
                                style={{ display: 'none' }}
                            />
                        ))}
                    </div>
                    <div ref={prevewImgBoxRef} className={style.prevewImgBox}>
                        <ImageOverlay
                            type="ImagineFace"
                            src={imgUrlsArr[currentPreviewIndex]}
                            style={{
                                position: 'relative',
                                width: '100%',
                                height: 'auto',
                                aspectRatio: '1/1.15',
                            }}
                            alt="Image alt text"
                        />
                    </div>
                </div>
                <div className={style.placeHodler} />
                <XScroll
                    customStyle={{
                        width: '100%',
                        height: '17%',
                    }}
                    selfid={'prodBoard'}
                    scrollBar={imgUrlsArr.length > 3}
                >
                    <div className={style.photoList}>
                        {sideColorArr.slice(0, imgUrlsArr.length)
                            .map((item, index) => (
                                <div
                                    className={style.photoImgBox}
                                    key={index}
                                    ref={photoItemRefs[index]}
                                    onClick={handlerClickPhoto.bind(null, index)}
                                >
                                    <ImageOverlay
                                        type="ImagineFace"
                                        src={imgUrlsArr[index]}
                                        style={{
                                            position: 'relative',
                                            width: '100%',
                                            height: 'auto',
                                            // maxHeight: '150px',
                                            aspectRatio: '1/1',
                                        }}
                                        alt="Image alt text"
                                    />
                                    <span
                                        className={style.normalItem}
                                        style={
                                            currentPreviewIndex === index
                                                ? {
                                                    backgroundColor: `${item}`,
                                                }
                                                : {
                                                    backgroundColor: 'rgb(180, 177, 175)',
                                                }
                                        }
                                    />
                                </div>
                            ))}
                    </div>
                </XScroll>
            </div>
        </div>
    );
}

export { Image3dProdBoard };
